import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {CommentBoxButton} from '@github-ui/comment-box/CommentBoxButton'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import {ScopedCommands} from '@github-ui/ui-commands'
import {CodeReviewIcon, CommentIcon, ReplyIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

import {usePersistedDiffCommentData} from '../../hooks/use-persisted-comment-data'
import type {DiffSide, SuggestedChangesConfiguration} from '../../conversation-types'
import {validateSuggestedChange} from '../../util/validate-suggested-change'
import styles from './AddCommentEditor.module.css'
import {ConversationCommentBox} from './ConversationCommentBox'
import {
  useComparisonInfo,
  useGetUpdatePersistedComment,
  useIsCommentBatchPending,
  usePullRequestBasePath,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useCopilotQuotaGate} from '@github-ui/pull-requests/hooks/use-copilot-quota-gate'
import {useDiffLinesActions} from '../../stores/selectors'
import type {DiffLineKey, DiffRange, FileKey, LineKey} from '../../types'
import {handleLineSelectionEffects} from '../../helpers/line-selection'
import {extractSuggestionFromMarkdown} from '../../helpers/extract-suggestion-content'
import {forceAnnouncementToScreenReaders} from '@github-ui/pull-requests/utils/force-announcement-to-screen-reader'
import {getDiffLineMarkersKey} from '../../helpers/line-helpers'

export interface AddCommentEditorProps {
  batchingEnabled?: boolean
  replyAndResolveEnabled?: boolean
  className?: string
  condensed: boolean
  fileLevelComment?: boolean
  filePath: string
  focusOnMount?: boolean
  isReplying?: boolean
  lineNumber?: number
  onAddComment?: (args: {
    commentText: string
    onCompleted?: () => void
    onError: (error: Error) => void
    submitBatch?: boolean
    /** Whether to resolve the thread after adding the comment - up to the caller to handle when replyAndResolveEnabled is true */
    resolveAfter?: boolean
  }) => void
  onCancelComment?: (shouldFocusCell?: boolean) => void
  onPersistedCommentExists?: () => void
  quotedText?: string
  ref?: React.Ref<AddCommentEditorHandle>
  side?: DiffSide
  startLineNumber?: number
  threadId?: string
  suggestedChangesConfig?: SuggestedChangesConfiguration
  commentLineChangeConfig?: CommentLineChangeConfig
  setStartLineNumber?: (lineNumber: number | undefined) => void
}

export interface AddCommentEditorHandle {
  /** Replaces the current comment text with the given quoted markdown, or appends it after the current draft when `append` is set. */
  insertQuote: (quotedText: string, options?: {append?: boolean}) => void
}

export interface CommentLineChangeConfig {
  diffSide: DiffSide
  endLineNumber: number
  endDiffLineKey: DiffLineKey
  isFileLevelComment: boolean
  startConversationOnNewLine: () => void
  closeLineMovingDialog: () => void
  setErrorMessage: (message: string) => void
  startDiffLineKey?: DiffLineKey
  startLineNumber?: number
}

export interface SelectMultipleLinesArgs {
  endLineKey: DiffLineKey | undefined
  endOrientation: 'left' | 'right'
  startLineKey: DiffLineKey | undefined
  startOrientation: 'left' | 'right'
  firstLineSelectionOnDrag: {
    lineKey: DiffLineKey
    lineNumber: number
    orientation: 'left' | 'right'
    shouldStartConversation: boolean
  }
}

interface AddCommentEditorNotDiffLinesProps extends AddCommentEditorProps {
  clearSelectedLines?: () => void
  comparisonRange?: DiffRange
  selectMultipleLines?: (args: SelectMultipleLinesArgs) => void
  updatePersistedCommentOnLine?: (lineKey: LineKey | FileKey, hasStoredComment: boolean) => void
}

// Define explicit action types
const SUBMISSION_ACTION = {
  COMMENT: 'comment',
  REVIEW: 'review',
  REPLY_AND_RESOLVE: 'reply_and_resolve',
} as const

type SubmissionAction = (typeof SUBMISSION_ACTION)[keyof typeof SUBMISSION_ACTION]

export function AddCommentEditorNotDiffLines({
  batchingEnabled,
  replyAndResolveEnabled,
  className,
  comparisonRange,
  condensed,
  fileLevelComment,
  filePath,
  focusOnMount,
  isReplying,
  lineNumber,
  onAddComment,
  onCancelComment,
  onPersistedCommentExists,
  quotedText = '',
  ref,
  side,
  startLineNumber,
  threadId,
  suggestedChangesConfig,
  commentLineChangeConfig,
  clearSelectedLines,
  selectMultipleLines,
  updatePersistedCommentOnLine,
}: AddCommentEditorNotDiffLinesProps) {
  const [commentText, setCommentText] = useState(quotedText)
  const [submittingAction, setSubmittingAction] = useState<SubmissionAction | null>(null)
  const quotedTextRef = useRef(quotedText)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const editorRef = useRef<CommentBoxHandle>(null)
  const primaryActionButtonRef = useRef<HTMLButtonElement>(null)
  const commentBatchPending = useIsCommentBatchPending()
  const previousCommentLineConfigRef = useRef<CommentLineChangeConfig | undefined>(undefined)
  const setErrorMessageWithScreenReaderAnnouncement = useCallback(
    (message: string | undefined) => {
      forceAnnouncementToScreenReaders(message ?? '', 100)
      setErrorMessage(message)
    },
    [setErrorMessage],
  )

  const isSubmitting = submittingAction !== null
  const basePath = usePullRequestBasePath()
  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(commentText)

  const batchIsPrimaryAction = batchingEnabled && (!isReplying || commentBatchPending)

  const {persistCommentToStorage, removePersistedCommentFromStorage, persistCommentOnGivenLineToStorage} =
    usePersistedDiffCommentData({
      diffSide: side,
      filePath,
      line: lineNumber,
      subjectId: basePath,
      threadId,
      handlePersistedCommentExists: ({text, shouldFocus, startLine, startSide, startLineKey, endLineKey}) => {
        if (!text) return
        if (quotedText) {
          setCommentText(`${text}\n\n${quotedText}`)
        } else {
          setCommentText(text)
        }
        if (shouldFocus && editorRef.current) {
          editorRef.current.focus()
          editorRef.current.moveCursorToEnd()
          persistCommentToStorage({text, startLine, startSide, startLineKey, endLineKey}) // update to ensure shouldFocus is removed
          if (startLine) {
            //if we should focus and there is a start line, it means we moved a multiline comment
            const lineOrientation = side === 'LEFT' ? 'left' : 'right'
            selectMultipleLines?.({
              endLineKey,
              endOrientation: lineOrientation,
              startLineKey,
              startOrientation: lineOrientation,
              firstLineSelectionOnDrag: {
                lineKey: startLineKey ?? 'b:0-l:0-r:0',
                lineNumber: lineNumber ?? 0,
                orientation: lineOrientation,
                shouldStartConversation: false,
              },
            })
          }
        }

        onPersistedCommentExists?.()
      },
      fileLevelComment: !!fileLevelComment,
    })

  useEffect(() => {
    if (quotedText !== quotedTextRef.current) {
      quotedTextRef.current = quotedText

      // The effect is only triggered when quotedText changes which only happens when "quote reply" is clicked
      // We maintain a ref to the previous quotedText to avoid unnecessary state updates mitigating performance concerns

      setCommentText(quotedText)
    }
  }, [quotedText])

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(text, {append = false} = {}) {
        setCommentText(prev => {
          const next = append && prev.trim().length > 0 ? `${prev}\n\n${text}` : text
          persistCommentToStorage({
            text: next,
            startLine: startLineNumber,
            startSide: startLineNumber ? side : undefined,
          })
          return next
        })
        // Keep the ref in sync so a later `quotedText` prop change that matches this value doesn't
        // no-op the useEffect above and silently drop content the caller pushed via this handle.
        quotedTextRef.current = text
        editorRef.current?.focus()
        editorRef.current?.moveCursorToEnd()
      },
    }),
    [persistCommentToStorage, side, startLineNumber],
  )

  useEffect(() => {
    if (commentLineChangeConfig) {
      if (extractSuggestionFromMarkdown(commentText) !== null) {
        commentLineChangeConfig.setErrorMessage(
          'Changing the line number of a comment with a suggested change is not supported.',
        )
        return
      }
      commentLineChangeConfig.closeLineMovingDialog()
      persistCommentOnGivenLineToStorage({
        text: commentText,
        givenLineDiffSide: commentLineChangeConfig.diffSide,
        givenFilePath: filePath,
        givenLine: commentLineChangeConfig.endLineNumber,
        shouldBeFileLevelComment: commentLineChangeConfig.isFileLevelComment,
        startLine: commentLineChangeConfig.startLineNumber,
        startLineKey: commentLineChangeConfig.startDiffLineKey,
        endLineKey: commentLineChangeConfig.endDiffLineKey,
      })

      removePersistedCommentFromStorage()
      if (lineNumber !== commentLineChangeConfig.endLineNumber || commentLineChangeConfig.isFileLevelComment) {
        // we don't want to cancel the current comment if the end line is the same
        commentLineChangeConfig.startConversationOnNewLine()
        onCancelComment?.(false)
      } else if (
        commentLineChangeConfig.endDiffLineKey &&
        commentLineChangeConfig.startDiffLineKey &&
        previousCommentLineConfigRef.current !== commentLineChangeConfig
      ) {
        // if the line number is the same and there is a different start line number, we need to modify the selection
        // in order to prevent infinite loops we need to track the previous comment line config. I don't love this
        // solution, but given how we have to manage the line selection in order to move multi-line comments, this was
        // necessary for changing the start line only of a multi-line comment, or moving a multi-ine comment back to
        // a single line comment.
        previousCommentLineConfigRef.current = commentLineChangeConfig
        const lineOrientation = side === 'LEFT' ? 'left' : 'right'
        selectMultipleLines?.({
          endLineKey: commentLineChangeConfig.endDiffLineKey,
          endOrientation: lineOrientation,
          startLineKey: commentLineChangeConfig.startDiffLineKey,
          startOrientation: lineOrientation,
          firstLineSelectionOnDrag: {
            lineKey: commentLineChangeConfig.startDiffLineKey ?? 'b:0-l:0-r:0',
            lineNumber: lineNumber ?? 0,
            orientation: lineOrientation,
            shouldStartConversation: false,
          },
        })
      } else if (!commentLineChangeConfig.startDiffLineKey) {
        clearSelectedLines?.()
      }
    }
  }, [
    clearSelectedLines,
    commentLineChangeConfig,
    commentText,
    filePath,
    lineNumber,
    onCancelComment,
    persistCommentOnGivenLineToStorage,
    removePersistedCommentFromStorage,
    selectMultipleLines,
    side,
  ])

  useEffect(() => {
    if (focusOnMount) {
      const timeout = setTimeout(() => {
        if (!ssrSafeDocument || !ssrSafeWindow) return

        if (editorRef.current) {
          // Focus first so the browser cannot cancel the following smooth scroll.
          editorRef.current.focus()

          // Move the cursor to the end of the comment text
          editorRef.current.moveCursorToEnd()
        }

        // If the primary action is not in view, scroll it into view, so the user can see the comment box
        if (primaryActionButtonRef.current) {
          const primaryActionButton = primaryActionButtonRef.current
          const primaryActionButtonRect = primaryActionButton.getBoundingClientRect()
          const documentClientHeight = ssrSafeDocument?.documentElement.clientHeight || 0
          const primaryActionButtonIsVisible =
            primaryActionButtonRect.top >= 0 &&
            primaryActionButtonRect.bottom <= (ssrSafeWindow?.innerHeight || documentClientHeight)

          if (!primaryActionButtonIsVisible) {
            // this element will always be rendered below the fold if not visible, so we need to scroll it into view
            // we want this to sit comfortably above the fold, so we scroll it to 2/3 of the way down the viewport
            const targetTop = primaryActionButtonRect.top
            const documentTop = ssrSafeDocument?.documentElement.getBoundingClientRect().top || 0
            const twoThirdsOfDocumentHeight = documentClientHeight * (2 / 3)
            const twoThirdsOfClientHeight = primaryActionButton.clientHeight * (2 / 3)
            const targetScroll = targetTop - documentTop - twoThirdsOfDocumentHeight + twoThirdsOfClientHeight

            ssrSafeWindow?.scrollTo({top: targetScroll, behavior: 'smooth'})
          }
        }
      }, 100)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [focusOnMount])

  const handleAddComment = ({
    submitBatch,
    action,
    resolve = false,
  }: {
    submitBatch: boolean
    action: SubmissionAction
    resolve?: boolean
  }) => {
    let msg = 'Failed to save comment'

    if (isSubmitting || quotaSubmitDisabled) return

    if (!commentText.trim()) {
      setErrorMessageWithScreenReaderAnnouncement(`${msg}: Body can't be blank`)
      return
    }

    setSubmittingAction(action)
    setErrorMessage(undefined)
    setErrorMessageWithScreenReaderAnnouncement(undefined)

    const suggestedChangedEvaluation = validateSuggestedChange(
      commentText,
      suggestedChangesConfig?.sourceContentFromDiffLines ?? '',
    )

    if (!suggestedChangedEvaluation.isValid) {
      setErrorMessageWithScreenReaderAnnouncement(suggestedChangedEvaluation.errorMessage)
      setErrorMessage(suggestedChangedEvaluation.errorMessage)
      setSubmittingAction(null)

      return
    }
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      if (lineKey) updatePersistedCommentOnLine?.(lineKey, false)
    }

    onAddComment?.({
      commentText,
      resolveAfter: resolve,
      onCompleted() {
        setSubmittingAction(null)
        closeCommentBox()
      },
      onError(error) {
        setSubmittingAction(null)

        //if we failed to save, we want to re-persist the comment state
        if (lineNumber !== undefined && !isReplying) {
          const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
          if (lineKey) updatePersistedCommentOnLine?.(lineKey, true)
        }
        if (error.message) {
          msg = `${msg}: ${error.message}`
        }
        setErrorMessageWithScreenReaderAnnouncement(msg)
      },
      submitBatch,
    })
  }

  const handleAddReviewCommentAsPending = () => {
    handleAddComment({submitBatch: false, action: SUBMISSION_ACTION.REVIEW})
  }
  const handleSubmitPendingComments = () => {
    handleAddComment({submitBatch: true, action: SUBMISSION_ACTION.COMMENT})
  }
  const handleReplyAndResolve = () => {
    handleAddComment({submitBatch: true, action: SUBMISSION_ACTION.REPLY_AND_RESOLVE, resolve: true})
  }

  const closeCommentBox = useCallback(() => {
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      if (lineKey) updatePersistedCommentOnLine?.(lineKey, false)
    }
    removePersistedCommentFromStorage()
    setCommentText('')
    onCancelComment?.()
  }, [lineNumber, isReplying, removePersistedCommentFromStorage, onCancelComment, side, updatePersistedCommentOnLine])
  const handleCommentTextChange = (text: string) => {
    setErrorMessageWithScreenReaderAnnouncement(undefined)
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      const hasText = text.trim().length > 0
      if (lineKey) updatePersistedCommentOnLine?.(lineKey, hasText)
    }
    persistCommentToStorage({text, startLine: startLineNumber, startSide: startLineNumber ? side : undefined})
    setCommentText(text)
  }

  const batchCommentButtonProps = batchingEnabled
    ? ({
        className: 'py-1 px-2',
        variant: batchIsPrimaryAction ? 'primary' : 'default',
        ref: batchIsPrimaryAction ? primaryActionButtonRef : undefined,
        leadingVisual: CodeReviewIcon,
        onClick: handleAddReviewCommentAsPending,
        loading: submittingAction === SUBMISSION_ACTION.REVIEW,
        inactive: isSubmitting || commentText.trim().length === 0 || quotaSubmitDisabled,
        children: commentBatchPending ? 'Add review comment' : 'Start a review',
      } as const)
    : undefined

  return (
    <ScopedCommands
      commands={{
        'pull-requests-conversations:submit-comment-and-review': handleSubmitPendingComments,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(styles.AddCommentEditor, className)}
        onKeyDown={event => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (event.key === 'Escape' && !commentText.trim()) {
            event.preventDefault()
            closeCommentBox()
          } else {
            event.stopPropagation()
          }
        }}
      >
        {quotaBanner}
        <ConversationCommentBox
          ref={editorRef}
          label="Add diff comment"
          value={commentText}
          onChange={handleCommentTextChange}
          onPrimaryAction={batchIsPrimaryAction ? handleAddReviewCommentAsPending : handleSubmitPendingComments}
          suggestedChangesConfig={suggestedChangesConfig}
          markdownErrorMessage={errorMessage}
          lineNumber={lineNumber}
          filePath={filePath}
          startLineNumber={startLineNumber}
          disabled={isSubmitting}
          comparisonRange={comparisonRange}
          className={clsx(
            styles.ConversationCommentBox,
            condensed || isReplying ? styles.hideMarkdownFooter : styles.showMarkdownFooter,
            isReplying && styles.isReplying,
            condensed && styles.condensed,
          )}
          surface="review"
        >
          {onCancelComment && (
            <CommentBoxButton className="py-1 px-2" variant="default" onClick={closeCommentBox}>
              Cancel
            </CommentBoxButton>
          )}
          {isReplying && replyAndResolveEnabled && !commentBatchPending && (
            <CommentBoxButton
              className="py-1 px-2"
              variant="default"
              onClick={handleReplyAndResolve}
              loading={submittingAction === SUBMISSION_ACTION.REPLY_AND_RESOLVE}
              inactive={isSubmitting || commentText.trim().length === 0 || quotaSubmitDisabled}
            >
              Reply and resolve comment
            </CommentBoxButton>
          )}
          {!batchIsPrimaryAction && batchCommentButtonProps && <CommentBoxButton {...batchCommentButtonProps} />}
          {!commentBatchPending && (
            <CommentBoxButton
              className="py-1 px-2"
              variant={batchIsPrimaryAction ? 'default' : 'primary'}
              leadingVisual={isReplying ? ReplyIcon : CommentIcon}
              onClick={handleSubmitPendingComments}
              ref={batchIsPrimaryAction ? undefined : primaryActionButtonRef}
              loading={submittingAction === SUBMISSION_ACTION.COMMENT}
              inactive={isSubmitting || commentText.trim().length === 0 || quotaSubmitDisabled}
            >
              {isReplying ? 'Reply' : 'Comment'}
            </CommentBoxButton>
          )}
          {batchIsPrimaryAction && batchCommentButtonProps && <CommentBoxButton {...batchCommentButtonProps} />}
        </ConversationCommentBox>
      </div>
    </ScopedCommands>
  )
}

export function AddCommentEditor(props: AddCommentEditorProps) {
  const {filePath} = props
  const updatePersistedComment = useGetUpdatePersistedComment()
  const {selectMultipleLines, clearSelectedLines} = useDiffLinesActions()
  const {effectiveRange: comparisonRange} = useComparisonInfo()

  return (
    <AddCommentEditorNotDiffLines
      {...props}
      clearSelectedLines={clearSelectedLines}
      selectMultipleLines={args => {
        selectMultipleLines(args, handleLineSelectionEffects)
      }}
      updatePersistedCommentOnLine={(lineKey, hasStoredComment) => {
        updatePersistedComment(filePath, lineKey, hasStoredComment)
      }}
      comparisonRange={comparisonRange}
    />
  )
}
